/**
 * 用于检测app在各个平台上是否安装，这里复用app-banner的逻辑
 * 能在到手q、微信、qq浏览器上 检测app是否安装
 *
 * 底层依赖于 \@tencent/txv-appbanner，有很多遗留逻辑
 *
 */

import NativeFactory, { NativePkg, NativePkgEvent, NativePkgOptions } from '@tencent/txv-appbanner/src/native.js';

/**
 * App 检测返回的状态码
 */
export enum State {
  UNINSTALLED = 0,
  OPENABLE = 1,
  NOT_SURE = 2,
  NEED_UPDATE = 3,
  DOWNLOADING = 10,
  DOWNLOADED = 11,
  PAUSE = 12,
  INSTALLING = 13,
  INSTALLED = 14,
  FAIL = 15,
  CANCEL = 16,
}

/**
 * App 检测返回的状态码对应的字段描述 map
 *
 * 状态码{@link State}
 */
export const STATE_TEXT_MAP: Record<number, string> = {
  0: 'UNINSTALLED',
  1: 'OPENABLE',
  2: 'NOTSURE',
  3: 'NEEDUPDATE',
  10: 'DOWNLOADING',
  11: 'DOWNLOADED',
  12: 'PAUSE',
  13: 'INSATLLING',
  14: 'INSATLLED',
  15: 'FAIL',
  16: 'CANCEL',
};

export type UABrowsers = string;
/**
 * App的基本包信息，用于在微信、手q等浏览器内拉起、下载、检测使用
 */
export type PackageInfo = Omit<NativePkgOptions, 'pkg' | 'pkgurl' | 'scheme' | 'confid' | 'uaname'> & {
  package: string;
  /**
   * 兼容逻辑
   *
   * 提供给检测器和下载器检测的scheme名称。。与拉起app的scheme名称用途不同
   *
   * 腾讯视频txvideo是现在的拉起协议，但使用`txvideo`传入到微信检测器中无法检测到app是否安装...
   */
  packageProtocol: string;
  /**
   * 渠道号，下载器需要用到
   */
  confid: string | number;
  uaname?: UABrowsers;
};

/**
 * 该类可获取app在各个webview内的安装状态
 * 包括手q、微信浏览器、腾讯视频webview等
 */
export class AppChecker {
  private options: PackageInfo;
  private state: State = State.NOT_SURE;
  private native: NativePkg;

  /**
   * @param opts - 传入App的包相关信息
   */
  constructor(options: PackageInfo) {
    const opts = { ...options };
    this.options = opts;

    opts.packageProtocol = opts.packageProtocol.replace(/:\/\/.*$/, '');

    const pkgurl = `${opts.packageProtocol}://can_open_me_if_install_and_regeister_this_scheme`;

    // 更换下参数名字
    const opt: NativePkgOptions = Object.assign(
      {
        pkg: opts.package,
        pkgurl,
        scheme: opts.packageProtocol,
        confid: opts.confid ?? '',
        uaname: '',
      },
      this.options,
    );

    // 通过调用每个webview或者浏览器的JS API判断
    // 目前支持 qq, mqq, weixin
    const native = NativeFactory(opt);
    this.native = native;
  }

  /**
   * 获取app的安装状态
   */
  getResult(): Promise<State> {
    return new Promise((resolve) => {
      this.on('ready', () => {
        this.state = this.native.state;
        resolve(this.state);
      });
    });
  }

  /**
   * 监听native对象的事件 progress和state_change
   * progress: 下载进度
   * state_change: state状态变化
   * ready: state可以使用
   */
  public on<T extends keyof NativePkgEvent>(event: T, callback: NativePkgEvent[T]): this {
    if (event === 'ready') {
      this.onReady(callback as NativePkgEvent['ready']);
    } else {
      this.native.on(event, callback);
    }
    return this;
  }

  public off<T extends keyof NativePkgEvent>(event: T, callback: NativePkgEvent[T]): this {
    this.native.off(event, callback);
    return this;
  }

  public once<T extends keyof NativePkgEvent>(event: T, callback: NativePkgEvent[T]): this {
    this.native.once(event, callback);
    return this;
  }

  public waitUntilReady(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.onReady(() => {
        resolve();
      });
    });
  }

  /**
   * 对ready事件做一下特殊处理
   */
  private onReady(callback: NativePkgEvent['ready']) {
    if (this.native.readyState) {
      callback();
    } else {
      this.native.once('ready', callback);
    }
  }

  get currentState(): State {
    return this.native.state;
  }

  get downloader(): NativePkg['downloader'] {
    return this.native.downloader;
  }
}
